import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { BspiHeader, BspiFooter } from '../bspi/HomeSections';
import './InsightPage.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Brand assets served from /public
const BRAND_LOGO = '/insights/liquidity-illusion/brand-logo.png';
const FOOTER_MARK = '/insights/liquidity-illusion/footer-mark.png';

// ---------------------------------------------------------------------------
// Document-head metadata (no react-helmet dep needed for a single article)
// ---------------------------------------------------------------------------
const setMeta = (attr, key, content) => {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};
const setCanonical = (href) => {
  if (!href) return;
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const useArticleHead = (article) => {
  useEffect(() => {
    if (!article) return;
    const prevTitle = document.title;
    document.title = article.page_title || `${article.title} | Black Star PI`;
    setMeta('name', 'description', article.description);
    setMeta('property', 'og:title', article.og_title || article.title);
    setMeta('property', 'og:description', article.og_description || article.description);
    setMeta('property', 'og:type', 'article');
    setMeta('property', 'og:url', article.canonical_url);
    if (article.linkedin_cover) {
      const abs = article.linkedin_cover.startsWith('http')
        ? article.linkedin_cover
        : window.location.origin + article.linkedin_cover;
      setMeta('property', 'og:image', abs);
      setMeta('name', 'twitter:image', abs);
    }
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', article.og_title || article.title);
    setMeta('name', 'twitter:description', article.og_description || article.description);
    setCanonical(article.canonical_url);
    return () => { document.title = prevTitle; };
  }, [article]);
};

// ---------------------------------------------------------------------------
// Toast helper
// ---------------------------------------------------------------------------
const useToast = () => {
  const timerRef = useRef();
  const [state, setState] = useState({ message: '', show: false });
  const notice = useCallback((message) => {
    clearTimeout(timerRef.current);
    setState({ message, show: true });
    timerRef.current = setTimeout(() => setState((s) => ({ ...s, show: false })), 2200);
  }, []);
  return { toast: state, notice };
};

// ---------------------------------------------------------------------------
// The article render — data-driven; supplied article shape from backend.
// isEditorial toggles the workspace toolbar (edit / save-draft / reset / PDF /
// PNG / LinkedIn share). Public visitors never see it.
// ---------------------------------------------------------------------------
export const InsightArticle = ({ article, isEditorial = false }) => {
  const { toast, notice } = useToast();
  const [editing, setEditing] = useState(false);
  const articleRef = useRef(null);
  const rootRef = useRef(null);
  const publicationRef = useRef(null);
  const linkedinCoverRef = useRef(null);
  const draftKey = `bspi-insight-draft-${article?.slug || 'unknown'}`;
  const originalRef = useRef(null);

  useArticleHead(article);

  // Restore local editorial draft (admin only)
  useEffect(() => {
    if (!isEditorial || !articleRef.current) return;
    originalRef.current = articleRef.current.innerHTML;
    try {
      const stored = localStorage.getItem(draftKey);
      if (stored) articleRef.current.innerHTML = stored;
    } catch { /* ignore */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditorial, article?.slug]);

  const toggleEdit = () => {
    setEditing((prev) => {
      const next = !prev;
      if (articleRef.current) articleRef.current.contentEditable = String(next);
      if (rootRef.current) rootRef.current.classList.toggle('editing', next);
      if (next && articleRef.current) articleRef.current.focus();
      return next;
    });
  };
  const saveDraft = () => {
    try {
      localStorage.setItem(draftKey, articleRef.current.innerHTML);
      notice('Draft saved in this browser');
    } catch { notice('Draft save unavailable'); }
  };
  const resetDraft = () => {
    if (!window.confirm('Reset the article to the published copy?')) return;
    try { localStorage.removeItem(draftKey); } catch { /* ignore */ }
    if (articleRef.current) articleRef.current.innerHTML = originalRef.current || '';
    notice('Published copy restored');
  };
  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(publicationRef.current.innerText);
      notice('Article text copied');
    } catch { notice('Copy unavailable in this browser'); }
  };
  const shareLinkedIn = () => {
    const url = article.canonical_url || window.location.href;
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      '_blank', 'noopener,noreferrer,width=720,height=680'
    );
  };
  const downloadPDF = async () => {
    notice('Preparing PDF…');
    try {
      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
        import('html2canvas'), import('jspdf'),
      ]);
      const source = publicationRef.current;
      const canvas = await html2canvas(source, { scale: 2, useCORS: true, backgroundColor: '#fffdf8' });
      const imgData = canvas.toDataURL('image/jpeg', 0.92);
      const pdf = new jsPDF({ unit: 'pt', format: [canvas.width * 0.5, canvas.height * 0.5] });
      pdf.addImage(imgData, 'JPEG', 0, 0, canvas.width * 0.5, canvas.height * 0.5);
      pdf.save(`${article.slug}.pdf`);
      notice('PDF downloaded');
    } catch (e) {
      notice('PDF export failed');
    }
  };
  const downloadPNG = async (targetRef, name, isCover = false) => {
    notice('Preparing PNG…');
    try {
      const { default: html2canvas } = await import('html2canvas');
      const source = targetRef.current;
      const originalDisplay = source.style.display;
      const originalLeft = source.style.left;
      if (isCover) {
        source.style.left = '0';
        source.style.display = 'grid';
      }
      const canvas = await html2canvas(source, {
        scale: isCover ? 1 : 1.5, useCORS: true, backgroundColor: '#fffdf8',
        width: isCover ? 1200 : undefined, height: isCover ? 627 : undefined,
      });
      source.style.left = originalLeft;
      source.style.display = originalDisplay;
      canvas.toBlob((blob) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = name;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(a.href), 1200);
        notice('PNG downloaded');
      }, 'image/png');
    } catch { notice('PNG export failed'); }
  };

  if (!article) {
    return (
      <div className="bspi-insight" style={{ padding: 40, textAlign: 'center', color: '#65706b' }}>
        Loading…
      </div>
    );
  }

  const hero = article.hero_image || '';
  const footerMark = FOOTER_MARK; // small brand mark for the footer
  const brand = BRAND_LOGO;

  return (
    <>
      <div className="bspi chrome-only"><BspiHeader /></div>
      <div className="bspi-insight" ref={rootRef}>
      {isEditorial && (
        <nav className="toolbar" aria-label="Publication controls" data-testid="editorial-toolbar">
          <button className="tool" data-action="edit" onClick={toggleEdit}>
            {editing ? 'Finish editing' : 'Edit article'}
          </button>
          <button className="tool save" data-action="save" onClick={saveDraft}>Save draft</button>
          <button className="tool" data-action="copy" onClick={copyText}>Copy text</button>
          <button className="tool share" data-action="share" onClick={shareLinkedIn}>Share on LinkedIn ↗</button>
          <button className="tool download" data-action="pdf" onClick={downloadPDF}>Download PDF ↓</button>
          <button className="tool download" data-action="png-full"
            onClick={() => downloadPNG(publicationRef, `${article.slug}-full.png`)}>Full PNG ↓</button>
          <button className="tool download" data-action="png-cover"
            onClick={() => downloadPNG(linkedinCoverRef, `${article.slug}-linkedin-cover.png`, true)}>LinkedIn Cover ↓</button>
          <button className="tool reset" data-action="reset" onClick={resetDraft}>Reset</button>
        </nav>
      )}

      <article className="page" id="publication" ref={publicationRef}>
        {/* Masthead */}
        <header className="masthead">
          <a className="brand-link"
             href="/" aria-label="Black Star PI home">
            <img src={brand} alt="Black Star PI" width="310" height="74" />
          </a>
          <div className="masthead-link">Executive Property Investment Insight</div>
        </header>

        {/* Hero */}
        <section className="hero">
          <div className="hero-copy">
            {article.category && <div className="eyebrow">{article.category}</div>}
            <h1>{article.title}</h1>
            {article.standfirst && <p className="standfirst">{article.standfirst}</p>}
            <div className="byline">
              {article.author && <>By <span className="author">{article.author}</span></>}
              {article.author && article.reading_time && <>&nbsp;&nbsp; · &nbsp;&nbsp;</>}
              {article.reading_time}
              {(article.author || article.reading_time) && article.issue && <>&nbsp;&nbsp; · &nbsp;&nbsp;</>}
              {article.issue && <span className="issue-inline">{article.issue}</span>}
            </div>
          </div>
          <figure className="hero-media">
            {hero && (
              <img src={hero} alt={article.hero_alt || article.title}
                   width="600" height="610" loading="eager" />
            )}
            {article.hero_caption && (
              <figcaption className="photo-caption">{article.hero_caption}</figcaption>
            )}
          </figure>
        </section>

        {/* Evidence */}
        {(article.evidence_headline || (article.evidence_metrics || []).length > 0) && (
          <section className="evidence" aria-label={article.evidence_label || 'Evidence panel'}>
            <div className="evidence-intro">
              {article.evidence_label && <div className="section-label">{article.evidence_label}</div>}
              {article.evidence_headline && <h2>{article.evidence_headline}</h2>}
              {article.evidence_summary && <p>{article.evidence_summary}</p>}
            </div>
            <div className="evidence-chart" role="img"
                 aria-label={article.evidence_metrics?.map(m => `${m.value} ${m.label}`).join(' versus ')}>
              {(article.evidence_metrics || []).map((m, i) => (
                <div className="chart-row" key={i}>
                  <div className="chart-value">{m.value}</div>
                  <div className="bar-track">
                    <div className={`bar-fill${m.colour === 'gold' ? ' gold' : ''}`}
                         style={{ width: `${Math.max(0, Math.min(100, m.width || 100))}%` }} />
                  </div>
                </div>
              ))}
              <div className="chart-key">
                {(article.evidence_metrics || []).map((m, i) => (
                  <span key={i}><i className={`dot${m.colour === 'gold' ? ' gold' : ''}`} />{m.label}</span>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Body */}
        <main>
          {article.executive_lead && <p className="lead">{article.executive_lead}</p>}
          <div className="content">
            <div className="article" id="editableArticle" ref={articleRef}>
              {(article.sections || []).map((s, i) => (
                <React.Fragment key={i}>
                  <h2>{s.heading}</h2>
                  <div dangerouslySetInnerHTML={{ __html: s.body_html }} />
                </React.Fragment>
              ))}
            </div>
            <aside>
              {article.perspective && (
                <div className="perspective">
                  <div className="aside-kicker">Black Star perspective.</div>
                  <p className="pull">{article.perspective}</p>
                </div>
              )}
              {(article.checklist_items || []).length > 0 && (
                <div className="checklist">
                  <h3>{article.checklist_title || 'Investor due diligence'}</h3>
                  {(article.checklist_items || []).map((item, i) => (
                    <div key={i}>{item}</div>
                  ))}
                </div>
              )}
            </aside>
          </div>
          {article.bottom_line && (
            <div className="bottom-line">
              <div className="label">Bottom line</div>
              <p>{article.bottom_line}</p>
            </div>
          )}
        </main>

        {/* Sources */}
        {(article.sources || []).length > 0 && (
          <section className="sources">
            <h2>Sources</h2>
            <ol>
              {article.sources.map((s, i) => (
                <li key={i}>
                  <a href={s.url} target="_blank" rel="noopener noreferrer"
                     dangerouslySetInnerHTML={{ __html: s.citation_html }} />
                  {s.date && <>, {s.date}.</>}
                </li>
              ))}
            </ol>
          </section>
        )}

        {/* Footer */}
        <footer className="insight-footer">
          <div className="footer-brand">
            <a className="footer-logo-link mark" href="/">
              <img className="footer-logo mark" src={footerMark} alt="Black Star PI"
                   width="74" height="62" />
            </a>
            <span className="footer-copy">Published by Black Star PI Ltd</span>
          </div>
          <a className="site-link" href="/">black-starpi.com</a>
        </footer>
      </article>

      {/* Off-screen LinkedIn cover for PNG export (1200x627) */}
      <section className="linkedin-cover" id="linkedinCover" ref={linkedinCoverRef} aria-hidden="true">
        <div className="cover-copy">
          <img className="cover-logo" src={brand} alt="Black Star PI" width="285" height="82" />
          <div>
            <div className="cover-title">{article.title}</div>
            <div className="cover-sub">{article.standfirst}</div>
            <div className="cover-footer">PROPERTY INVESTMENT INSIGHT · {article.issue?.split('·')[0]?.trim() || 'ISSUE 01'}</div>
          </div>
        </div>
        <img className="cover-photo" src={hero} alt={article.hero_alt || ''} />
      </section>

      <div className={`toast${toast.show ? ' show' : ''}`} role="status">{toast.message}</div>
      </div>
      <div className="bspi chrome-only"><BspiFooter /></div>
    </>
  );
};

// Route wrapper: fetches the article by slug and shows a "not found" state
// when the slug doesn't exist.
export const InsightPage = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/public/insights/${slug}`);
        if (!alive) return;
        if (res.ok) {
          setArticle(await res.json());
          setStatus('ok');
        } else {
          setStatus('notfound');
        }
      } catch {
        if (alive) setStatus('error');
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  if (status === 'loading') {
    return <div className="bspi-insight" style={{ padding: 60, textAlign: 'center' }}>Loading…</div>;
  }
  if (status !== 'ok') {
    return (
      <div className="bspi-insight" style={{ padding: 60, textAlign: 'center' }}>
        <div className="page" style={{ padding: 48 }}>
          <h2 style={{ fontFamily: 'Courier New, monospace', color: '#062f2a' }}>Article not found</h2>
          <p style={{ color: '#65706b' }}>
            The article you were looking for isn't available. <Link to="/insights" style={{ color: '#b78a2e' }}>Back to Insights</Link>
          </p>
        </div>
      </div>
    );
  }
  return <InsightArticle article={article} isEditorial={false} />;
};

export default InsightPage;
